/* eslint-disable @typescript-eslint/no-unsafe-member-access */
import { createApp } from "vue";
import type { App as VueApp } from "vue";
import { createPinia } from "pinia";
import VueMatomo from "vue-matomo";
import moment from "moment";
import "moment/locale/fr";
import "moment/locale/de";

import CoreLdeVue from "@lde/core_lde_vue";

import i18n from "@/modules/i18n";
import matomoConfig, { matomoLogClickDirective, handleMatomoBlocked } from "@/modules/matomo";
import titleOverflowDirective from "@/modules/title_overflow";
import router from "@/router/index.js";
import store from "@/store/index.js";
import Sentry from "@/modules/sentry";
import AppComponent from "@/App.vue";
import PageLayout from "@/components/layout/PageLayout.vue";

moment.locale("fr");
moment.locale("de");

const app: VueApp = createApp(AppComponent);

Sentry.setApp(app);

// Importe tous les composants génériques et les plugins de CoreLdeVue
app.use(i18n);
app.use(CoreLdeVue, { i18n });
app.use(router);
app.use(createPinia());
app.use(store);

app.use(VueMatomo, matomoConfig);
if (!app.config.globalProperties.$matomo) {
  handleMatomoBlocked(app);
}

app.directive("matomo-log-click", matomoLogClickDirective);
app.directive("title-overflow", titleOverflowDirective);
app.component("PageLayout", PageLayout);

// Rendre moment disponible globalement via $moment
app.config.globalProperties.$moment = moment;

// Masquer certains avertissements spécifiques durant la migration Vue 3
app.config.warnHandler = (msg, instance, trace) => {
  // Masquer les avertissements sur les composants marqués comme réactifs
  if (msg.includes("Component that was made a reactive object")) {
    return;
  }

  if (msg.includes("was accessed during render but is not defined on instance")) {
    const stack = new Error().stack;

    // Vérifier si "mixin" est présent dans la stacktrace:
    // Les mixins c'est le mal, donc on ignore ces avertissements pour l'instant
    if (stack && stack.includes("mixin")) {
      return;
    }
  }

  // Afficher les autres avertissements
  console.warn(`[LDE Vue warn]: ${msg}`, trace);
};

// Mixin global pour isImpersonating
app.mixin({
  computed: {
    isImpersonating(): boolean {
      return this.$store.getters.impersonating as boolean;
    },
  },
});

// =========== Gestion de la mise à jour du cache via WS ===========

if ("serviceWorker" in navigator && window.location.protocol === "https:") {
  window.addEventListener("load", () => {
    navigator.serviceWorker
      .register("/service-worker.js")
      .then((registration: ServiceWorkerRegistration) => {
        registration.addEventListener("updatefound", () => {
          // Si un nouveau Service Worker est en attente
          if (registration.installing) {
            const newWorker = registration.installing;

            newWorker.addEventListener("statechange", () => {
              if (newWorker.state === "installed" && registration.waiting) {
                // Un nouveau Service Worker est disponible, forcer à rafraîchir la page
                caches.keys().then((cacheNames: string[]) => {
                  cacheNames.forEach((cacheName: string) => {
                    caches.delete(cacheName).catch((error: Error) => {
                      console.error("Erreur lors de la suppression du cache : ", error);
                    });
                  });
                }).catch((error: Error) => {
                  console.error("Erreur lors de la récupération du cache : ", error);
                });

                registration.waiting?.postMessage({ action: "skipWaiting" });
              }
            });
          }
        });
      }).catch((error: Error) => {
        console.error("Erreur lors de l'enregistrement du Service Worker : ", error);
      });
  });

  navigator.serviceWorker.addEventListener("controllerchange", () => {
    window.location.reload();
  });
}

// =======================================================

app.mount("body");

export default app;
